import React, { useLayoutEffect, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import "./PaymentForm.scss";
import classNames from "classnames";
import Validations from "../validations/Validations";
import { createSubscription } from "../../axios/CreateSubscriptionFacade";

import { prices } from "../../utils/stripeUtils";
import Banner from "../banner/banner";
import { bannerSentiment } from "../banner/bannerUtils";
import {
  storeSubscription,
  storeUserCustomerId
} from "../../actions/userActions";
import { useAppSelector, useAppDispatch } from "../../hooks";

/**
 * Submits the card details and creates the subscription. Resolves to whether
 * an error occurred, which is what `renderButtons` acts on.
 */
export type ConfirmPayment = () => Promise<boolean | undefined>;

export interface PaymentFormProps {
  /** Dollars per month, 1-9. */
  premiumAmount: number;
  setIsProcessing: (isProcessing: boolean) => void;
  renderButtons?: (confirmPayment: ConfirmPayment) => React.ReactNode;
}

export default function PaymentForm({
  premiumAmount,
  setIsProcessing,
  renderButtons
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [hasFocus, setHasFocus] = useState(false);
  const [error, setError] = useState("");
  const [subscriptionCreationError, setSubscriptionCreationError] = useState(
    ""
  );

  const { customerId, uid, email } = useAppSelector(state => state.userDetails);
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (elements) {
      const cardElement = elements.getElement(CardElement);
      cardElement?.on("focus", () => setHasFocus(true));
      cardElement?.on("blur", () => setHasFocus(false));
      cardElement?.on("change", () => setError(""));
    }
  }, [setHasFocus, elements]);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }
    setIsProcessing(true);

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setIsProcessing(false);
      return;
    }

    // Use your card Element with other Stripe.js APIs
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement
    });

    let errorOccurred;
    if (error || !paymentMethod) {
      setError(error?.message ?? "Could not read those card details.");
      errorOccurred = true;
    } else {
      const price_id = prices[premiumAmount - 1];
      try {
        const response = await createSubscription(
          customerId,
          email,
          price_id,
          paymentMethod.id
        );
        if (!customerId) {
          dispatch(
            storeUserCustomerId({ uid, customerId: response.data.customerId })
          );
        }
        dispatch(storeSubscription(uid, response.data.subscriptionId));
        errorOccurred = false;
      } catch (err) {
        setSubscriptionCreationError(
          "Error occurred during processing. Please contact the Dashboi team @ dashboi.app@gmail.com"
        );
        errorOccurred = true;
      }
    }
    setIsProcessing(false);
    return errorOccurred;
  };

  return (
    <div className="payment-form">
      {subscriptionCreationError && (
        <Banner
          sentiment={bannerSentiment.NEGATIVE}
          message={subscriptionCreationError}
        />
      )}
      <CardElement
        className={classNames("card-input", hasFocus ? "has-focus" : "")}
        options={{
          style: {
            base: {
              fontSize: "16px",
              color: "rgb(107, 36, 80)",
              "::placeholder": {
                color: "#575C5B"
              },
              fontFamily: '"Lato", sans-serif'
            },
            invalid: {
              color: "#D11D1A"
            }
          }
        }}
      />
      <Validations validations={[error]} />
      {renderButtons && renderButtons(handleSubmit)}
    </div>
  );
}
